import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { LanguageProvider, useLanguage } from "@/context/LanguageContext";
import LanguageGate from "@/pages/LanguageGate";
import MissingConfig from "@/pages/MissingConfig";
import Home from "@/pages/Home";
import CookieConsent from "@/components/site/CookieConsent";
import AnalyticsLoader from "@/components/site/AnalyticsLoader";

// Lazy-loaded non-critical routes — keeps the home bundle lean for mobile.
const KidsFarm = lazy(() => import("@/pages/KidsFarm"));
const ActivityDetail = lazy(() => import("@/pages/ActivityDetail"));
const Contact = lazy(() => import("@/pages/Contact"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const Terms = lazy(() => import("@/pages/Terms"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const Partners = lazy(() => import("@/pages/Partners"));
const PartnerActivity = lazy(() => import("@/pages/PartnerActivity"));
const SaltySpirit = lazy(() => import("@/pages/SaltySpirit"));
const PartnerWithUs = lazy(() => import("@/pages/PartnerWithUs"));
const Refunds = lazy(() => import("@/pages/Refunds"));
const AdminRefunds = lazy(() => import("@/pages/AdminRefunds"));

function PageSkeleton() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#FAF7F2]">
      <div className="h-10 w-10 animate-spin rounded-full border-2 border-[#E5DACC] border-t-[#FF5533]" />
    </div>
  );
}

function Gated({ children }) {
  const { lang, ready } = useLanguage();
  if (!ready) return <PageSkeleton />;
  if (!lang) return <LanguageGate />;
  return (
    <>
      <Suspense fallback={<PageSkeleton />}>{children}</Suspense>
      <CookieConsent />
    </>
  );
}

function App() {
  if (!process.env.REACT_APP_BACKEND_URL) return <MissingConfig />;
  return (
    <LanguageProvider>
      <AnalyticsLoader />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Gated><Home /></Gated>} />
          <Route path="/kids-farm" element={<Gated><KidsFarm /></Gated>} />
          <Route path="/horseback-beginner" element={<Gated><ActivityDetail /></Gated>} />
          <Route path="/horseback-family" element={<Gated><ActivityDetail /></Gated>} />
          <Route path="/horseback-sunset" element={<Gated><ActivityDetail /></Gated>} />
          <Route path="/snorkel-black-eagle" element={<Gated><ActivityDetail /></Gated>} />
          <Route path="/blog" element={<Gated><Blog /></Gated>} />
          <Route path="/blog/:slug" element={<Gated><BlogPost /></Gated>} />
          <Route path="/partners" element={<Gated><Partners /></Gated>} />
          <Route path="/luxury" element={<Gated><SaltySpirit /></Gated>} />
          <Route path="/partner/:slug" element={<Gated><PartnerActivity /></Gated>} />
          <Route path="/contact" element={<Gated><Contact /></Gated>} />
          <Route path="/partner-with-us" element={<Gated><PartnerWithUs /></Gated>} />
          <Route path="/partner-request" element={<Navigate to="/partner-with-us" replace />} />
          <Route path="/refunds" element={<Gated><Refunds /></Gated>} />
          <Route path="/refund-guide" element={<Navigate to="/refunds" replace />} />
          <Route path="/admin/refunds" element={<Gated><AdminRefunds /></Gated>} />
          <Route path="/admin/partners" element={<Gated><AdminRefunds initialTab="partners" /></Gated>} />
          <Route path="/privacy" element={<Gated><Privacy /></Gated>} />
          <Route path="/terms" element={<Gated><Terms /></Gated>} />
          <Route path="*" element={<Gated><Home /></Gated>} />
        </Routes>
      </BrowserRouter>
    </LanguageProvider>
  );
}

export default App;
